import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import LotesList from './pages/LotesList';
import LoteNew from './pages/LoteNew';
import LoteEdit from './pages/LoteEdit';
import LoteDetail from './pages/LoteDetail';
import ExpensesList from './pages/ExpensesList';
import ExpenseNew from './pages/ExpenseNew';
import ExpenseEdit from './pages/ExpenseEdit';
import FarmRecord from './pages/FarmRecord';
import Nomina from './pages/Nomina';
import EmpleadoForm from './pages/EmpleadoForm';
import PagoNominaForm from './pages/PagoNominaForm';
import PagoDetail from './pages/PagoDetail';
import Ventas from './pages/Ventas';
import VentaForm from './pages/VentaForm';
import VentaRapida from './pages/VentaRapida';
import Proveedores from './pages/Proveedores';
import ComparacionAlimento from './pages/ComparacionAlimento';
import LoteGeneral from './pages/LoteGeneral';
import GestionUsuarios from './pages/GestionUsuarios';

// Protected Route component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner h-8 w-8 border-2" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route - redirect to dashboard if already logged in
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner h-8 w-8 border-2" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lotes"
        element={
          <ProtectedRoute>
            <LotesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lotes/nuevo"
        element={
          <ProtectedRoute>
            <LoteNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lotes/:id"
        element={
          <ProtectedRoute>
            <LoteDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lotes/:id/editar"
        element={
          <ProtectedRoute>
            <LoteEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gastos"
        element={
          <ProtectedRoute>
            <ExpensesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gastos/nuevo"
        element={
          <ProtectedRoute>
            <ExpenseNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gastos/editar/:id"
        element={
          <ProtectedRoute>
            <ExpenseEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lotes/:id/record"
        element={
          <ProtectedRoute>
            <FarmRecord />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lotes/:loteId/comparacion-alimento"
        element={
          <ProtectedRoute>
            <ComparacionAlimento />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nomina"
        element={
          <ProtectedRoute>
            <Nomina />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nomina/empleado/nuevo"
        element={
          <ProtectedRoute>
            <EmpleadoForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nomina/empleado/:id"
        element={
          <ProtectedRoute>
            <EmpleadoForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nomina/pago/nuevo"
        element={
          <ProtectedRoute>
            <PagoNominaForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nomina/pago/:id"
        element={
          <ProtectedRoute>
            <PagoDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ventas"
        element={
          <ProtectedRoute>
            <Ventas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ventas/nueva"
        element={
          <ProtectedRoute>
            <VentaForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ventas/rapida"
        element={
          <ProtectedRoute>
            <VentaRapida />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proveedores"
        element={
          <ProtectedRoute>
            <Proveedores />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lote-general"
        element={
          <ProtectedRoute>
            <LoteGeneral />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usuarios"
        element={
          <ProtectedRoute>
            <GestionUsuarios />
          </ProtectedRoute>
        }
      />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
